import { Component, Inject, OnInit } from '@angular/core';
import {FormArray, FormBuilder, FormGroup, Validators} from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { CompaniesService } from '../../../services/modules/companies.service';

const UNIT_MAPPING = {
  DIA: 0,
  MES: 1,
  AÑO: 2
};

@Component({
  selector: 'app-dialog-requirement-configuration',
  templateUrl: './dialog-requirement-configuration.component.html',
  styleUrls: ['./dialog-requirement-configuration.component.scss']
})
export class DialogRequirementConfigurationComponent implements OnInit {
  adminInfoId: string;
  requirementId: string;
  configForm: FormGroup;
  isLoading = false;
  firstRowCleared = false;

  units = [
    { value: 'DIA', label: 'DIA' },
    { value: 'MES', label: 'MES' },
    { value: 'AÑO', label: 'AÑO' }
  ];

  constructor(
    public dialogRef: MatDialogRef<DialogRequirementConfigurationComponent>,
    private fb: FormBuilder,
    private companiesService: CompaniesService,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.configForm = this.fb.group({
      items: this.fb.array([])
    });
    this.requirementId = this.data?.Id || this.data?.requirementId;
    this.adminInfoId = this.data?.companyId || this.data?.adminInfoId;
  }

  ngOnInit(): void {
    while (this.items.length !== 0) { this.items.removeAt(0); }

    if (this.data?.configurations?.length) {
      for (const config of this.data.configurations) {
        const unitName = this.getUnitNameFromValue(config.Unit);
        this.items.push(this.createItem(config.Duration, unitName, config.Id));
      }
    } else {
      this.items.push(this.createItem(null, null, null));
    }
    this.makeFirstRowOptional();
  }

  private makeFirstRowOptional(): void {
    const g = this.items.at(0) as FormGroup;
    if (!g) { return; }
    g.get('quantity')?.clearValidators();
    g.get('unit')?.clearValidators();
    g.get('quantity')?.updateValueAndValidity({ emitEvent: false });
    g.get('unit')?.updateValueAndValidity({ emitEvent: false });
  }

  clearFirstRow(): void {
    const g = this.items.at(0) as FormGroup;
    if (!g) { return; }
    g.patchValue({ quantity: null, unit: null });
    g.markAsPristine();
    g.markAsUntouched();
    this.firstRowCleared = true;
  }

  getUnitNameFromValue(unitValue: number): string {
    switch (unitValue) {
      case 0:
        return 'DIA';
      case 1:
        return 'MES';
      case 2:
        return 'AÑO';
      default:
        return null;
    }
  }

  createItem(quantity: number | null = null, unit: string | null = null, configId: string | null = null): FormGroup {
    return this.fb.group({
      quantity: [quantity, [Validators.required, Validators.min(1)]],
      unit: [unit, [Validators.required]],
      configId: [configId]
    });
  }

  addRow(): void {
    this.items.push(this.createItem(null, null, null));
  }

  get items(): FormArray {
    return this.configForm.get('items') as FormArray;
  }

  preventInvalid(event: KeyboardEvent) {
    if (event.key === '-' || (event.key === '0' && (event.target as HTMLInputElement).value === '')) {
      event.preventDefault();
    }
  }

  removeRow(index: number): void {
    const item = this.items.at(index);
    const configId = item.get('configId')?.value;

    if (configId) {
      this.companiesService.deleteRequirementsConfigured(configId).subscribe(
        () => {
          this.items.removeAt(index);
          if (this.items.length === 0) {
            this.addRow();
          }
        },
        () => {
          this.items.removeAt(index);
          if (this.items.length === 0) {
            this.addRow();
          }
        }
      );
    } else {
      this.items.removeAt(index);
      if (this.items.length === 0) {
        this.addRow();
      }
    }
  }

  async save(): Promise<void> {
    let removedFirstTemporarily = false;
    try {
      const firstGroup = this.items.at(0) as FormGroup | null;
      if (firstGroup) {
        const q = firstGroup.get('quantity')?.value;
        const u = firstGroup.get('unit')?.value;
        const id = firstGroup.get('configId')?.value;

        const isEmpty = (q === null || q === '' || q === undefined) &&
          (u === null || u === '' || u === undefined);

        if (isEmpty && id) {
          try {
            await this.companiesService.deleteRequirementsConfigured(id).toPromise();
            firstGroup.get('configId')?.setValue(null, { emitEvent: false });
          } catch (e) { }
        }

        if (isEmpty) {
          this.items.removeAt(0);
          removedFirstTemporarily = true;
        }
      }
    } catch {}

    if (this.configForm.valid && !this.isLoading) {
      this.isLoading = true;
      const formData = this.items.getRawValue();
      const savePromises = formData.map(item => {
        const payload = {
          duration: item.quantity,
          unit: UNIT_MAPPING[item.unit],
          administratorId: this.adminInfoId
        };
        return item.configId
          ? this.companiesService.putRequirementsConfigured(item.configId, payload).toPromise()
          : this.companiesService.postRequirementsConfigured(this.requirementId, payload).toPromise();
      });

      Promise.all(savePromises).then(() => {
        this.isLoading = false;
        this.dialogRef.close(true);
      }).catch(() => {
        this.isLoading = false;
        this.dialogRef.close(false);
      }).finally(() => {
        if (removedFirstTemporarily && this.items.length === 0) {
          this.items.push(this.createItem(null, null, null));
        } else if (removedFirstTemporarily) {
          this.items.insert(0, this.createItem(null, null, null));
        }
      });
    }
  }

  close(): void {
    this.dialogRef.close(false);
  }
}
